import { getCookie, setCookie } from "./utils/cookies"

import { SDKOptions } from "./sdk"

export type TState = {
    consumerToken?: string
    customerWeakToken?: string
    customerStrongToken?: string
    customer_id?: string
    email?: string
    cart_id?: string
    cart_access_token?: string
    historic_cart_id?: string
    historic_cart_access_token?: string
}

export class State {
    #options: SDKOptions
    #storage?: Storage
    #internalState: TState
    #listeners = new Set<() => void>()
    subscribe: (onStoreChange: () => void) => () => void
    getSnapshot: () => TState
    getServerSnapshot: () => TState

    constructor(options: SDKOptions, storage?: Storage) {
        this.#options = options
        this.#storage = storage

        this.#internalState = this.dehydrate()

        this.subscribe = (listener: () => void) => {
            this.#listeners.add(listener)
            return () => {
                this.#listeners.delete(listener)
            }
        }

        this.getSnapshot = () => {
            const state = this.dehydrate()
            if (this.deepEqual(state, this.#internalState)) return this.#internalState

            this.#internalState = state
            return state
        }

        this.getServerSnapshot = () => {
            return this.#internalState
        }
    }

    // ACCESSORS =================================================================

    get consumerToken() {
        return this.#internalState.consumerToken
    }

    set consumerToken(token) {
        this.partialUpdate({ consumerToken: token })
    }

    get customerWeakToken() {
        return this.getData("customerWeakToken")
    }

    set customerWeakToken(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customerWeakToken`, token, this.#options.crossDomain)
        }
        this.partialUpdate({ customerWeakToken: token })
    }

    get customerStrongToken() {
        return this.getData("customerStrongToken")
    }

    set customerStrongToken(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customerStrongToken`, token, this.#options.crossDomain)
        }
        this.partialUpdate({ customerStrongToken: token })
    }

    // CUSTOMER ==================================================================

    get customer_id() {
        return this.getData("customer_id")
    }

    set customer_id(id) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-customer_id`, id, this.#options.crossDomain)
        }
        this.partialUpdate({ customer_id: id })
    }

    get email() {
        return this.#internalState.email
    }

    set email(email) {
        this.partialUpdate({ email: email })
    }

    // CART ======================================================================

    get cart_id() {
        return this.getData("cart_id")
    }

    set cart_id(id) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-cart_id`, id, this.#options.crossDomain)
        }
        this.partialUpdate({ cart_id: id })
    }

    get cart_access_token() {
        return this.getData("cart_access_token")
    }

    set cart_access_token(token) {
        if (this.#options.crossDomain) {
            setCookie(`${this.#options.storageKey}-cart_access_token`, token, this.#options.crossDomain)
        }
        this.partialUpdate({ cart_access_token: token })
    }

    get historic_cart_id() {
        return this.#internalState.historic_cart_id
    }

    set historic_cart_id(id) {
        this.partialUpdate({ historic_cart_id: id })
    }

    get historic_cart_access_token() {
        return this.#internalState.historic_cart_access_token
    }

    set historic_cart_access_token(token) {
        this.partialUpdate({ historic_cart_access_token: token })
    }

    // HELPER METHODS ============================================================

    partialUpdate(state: TState) {
        this.#internalState = Object.assign({}, this.#internalState, state)

        this.hydrate(this.#internalState)
        this.#listeners.forEach(listener => {
            listener()
        })
    }

    getData(key: keyof TState) {
        if (!this.#options.crossDomain) return this.#internalState[key]

        return getCookie(`${this.#options.storageKey}-${key}`)
    }

    deepEqual(a: any, b: any): boolean {
        return a && b && typeof a === "object" && typeof a === typeof b
            ? Object.keys(a).length === Object.keys(b).length &&
                  Object.keys(a).every(key => this.deepEqual(a[key], b[key]))
            : a === b
    }

    // STORAGE METHODS ===========================================================

    hydrate(state: TState) {
        if (!this.#storage) return

        this.#storage.setItem(this.#options.storageKey, JSON.stringify(state))
    }

    dehydrate() {
        if (!this.#storage) return {}

        const nativeValue = this.#storage.getItem(this.#options.storageKey)
        if (!nativeValue) return {}

        try {
            let state = JSON.parse(nativeValue)
            if (this.#options.crossDomain) {
                state = Object.assign(state, {
                    customerWeakToken: this.getData("customerWeakToken"),
                    customerStrongToken: this.getData("customerStrongToken"),
                    customer_id: this.getData("customer_id"),
                    cart_id: this.getData("cart_id"),
                    cart_access_token: this.getData("cart_access_token"),
                })
            }
            return state
        } catch (err) {
            return {}
        }
    }
}
